export const REEL_DOWNLOAD_FILENAME = 'scoutlytics-studio-reel.mp4';
export const REEL_DOWNLOAD_MIME = 'video/mp4';
export const REEL_FETCH_TIMEOUT_MS = 120_000;
/** Skip in-memory Web Share above this size — iOS heap risk. */
export const REEL_SHARE_MAX_BYTES = 200 * 1024 * 1024;
export const REEL_SHARE_TITLE = 'Scoutlytics Studio reel';

export type ReelFetchProgress = {
  loaded: number;
  total: number | null;
};

type ShareNav = Navigator & {
  canShare?: (data: ShareData) => boolean;
  standalone?: boolean;
};

function mediaMatches(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

function isStandalonePwa(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as ShareNav;
  return nav.standalone === true || mediaMatches('(display-mode: standalone)');
}

/** Phones, tablets, and installed PWAs — not a fine-pointer desktop. */
export function prefersMobileReelSave(): boolean {
  if (typeof window === 'undefined') return false;
  if (isStandalonePwa()) return true;
  return mediaMatches('(pointer: coarse)');
}

export function isShareAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const name = (error as { name?: string }).name;
  return name === 'AbortError';
}

export function parseReelContentLength(
  headers: { get?(name: string): string | null } | undefined
): number | null {
  const raw = headers?.get?.('content-length');
  if (raw == null || raw === '') return null;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/** "Saving…" or "Saving… 42%" when Content-Length is known. */
export function reelSaveWorkingLabel(
  workingText: string,
  progress: ReelFetchProgress | null
): string {
  if (!progress || progress.total == null || progress.total <= 0) {
    return workingText;
  }
  const pct = Math.min(
    100,
    Math.max(0, Math.floor((progress.loaded / progress.total) * 100))
  );
  return `${workingText} ${pct}%`;
}

export function reelSaveProgressIndeterminate(
  progress: ReelFetchProgress | null
): boolean {
  return progress == null || progress.total == null || progress.total <= 0;
}

function probeFile(): File {
  return new File([new Uint8Array(1)], REEL_DOWNLOAD_FILENAME, {
    type: REEL_DOWNLOAD_MIME,
  });
}

export function canShareReelFile(file: File = probeFile()): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as ShareNav;
  if (typeof nav.share !== 'function' || typeof nav.canShare !== 'function') {
    return false;
  }
  try {
    return nav.canShare({ files: [file] });
  } catch {
    return false;
  }
}

export function triggerAnchorDownload(
  url: string,
  filename: string = REEL_DOWNLOAD_FILENAME
): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export function openReelInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

function mimeFromParts(headerType: string, blobType?: string): string {
  if (headerType && headerType.toLowerCase().startsWith('video/')) {
    return headerType;
  }
  if (blobType && blobType.toLowerCase().startsWith('video/')) {
    return blobType;
  }
  return REEL_DOWNLOAD_MIME;
}

function fileFromParts(parts: BlobPart[], type: string): File {
  // One copy into a File — avoid Blob-then-File double buffering on iOS.
  return new File(parts, REEL_DOWNLOAD_FILENAME, { type });
}

async function cancelResponseBody(response: Response): Promise<void> {
  try {
    if (response.body && typeof response.body.cancel === 'function') {
      await response.body.cancel();
    }
  } catch {
    // Best-effort — do not block the open-in-tab fallback.
  }
}

async function readResponseFile(
  response: Response,
  onProgress?: (progress: ReelFetchProgress) => void
): Promise<File> {
  const total = parseReelContentLength(response.headers);
  if (total != null && total > REEL_SHARE_MAX_BYTES) {
    await cancelResponseBody(response);
    throw new Error('reel exceeds in-memory share limit');
  }

  const headerType = response.headers?.get?.('content-type') || '';
  const reader =
    response.body && typeof response.body.getReader === 'function'
      ? response.body.getReader()
      : null;

  if (!reader) {
    onProgress?.({ loaded: 0, total });
    const blob = await response.blob();
    const type = mimeFromParts(headerType, blob.type);
    onProgress?.({ loaded: blob.size, total: total ?? blob.size });
    try {
      return fileFromParts([blob], type);
    } catch (error) {
      throw error instanceof Error ? error : new Error('reel blob assembly failed');
    }
  }

  const chunks: BlobPart[] = [];
  let loaded = 0;
  onProgress?.({ loaded: 0, total });
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value || value.byteLength === 0) continue;
      // Stream chunks are Uint8Array<ArrayBufferLike> in TS 5.x; File still accepts them.
      chunks.push(value as BlobPart);
      loaded += value.byteLength;
      if (loaded > REEL_SHARE_MAX_BYTES) {
        try {
          await reader.cancel();
        } catch {
          // ignore
        }
        throw new Error('reel exceeds in-memory share limit');
      }
      onProgress?.({ loaded, total });
    }
    const type = mimeFromParts(headerType);
    return fileFromParts(chunks, type);
  } catch (error) {
    chunks.length = 0;
    throw error instanceof Error ? error : new Error('reel blob assembly failed');
  }
}

async function fetchReelFile(
  url: string,
  onProgress?: (progress: ReelFetchProgress) => void
): Promise<File> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), REEL_FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`reel fetch ${response.status}`);
    }
    return await readResponseFile(response, onProgress);
  } finally {
    window.clearTimeout(timer);
  }
}

export type SaveReelResult = 'downloaded' | 'shared' | 'opened' | 'cancelled';

/**
 * Capability-branched reel save:
 * mobile + file share → Web Share sheet (Save Video / Save to Files);
 * mobile without share, oversized reel, CORS/fetch failure, or timeout →
 * open in a new tab (caller shows the long-press hint);
 * desktop → `<a download>` click.
 */
export async function saveReelToDevice(
  url: string,
  onProgress?: (progress: ReelFetchProgress) => void
): Promise<SaveReelResult> {
  const trimmed = url.trim();
  if (!trimmed) return 'cancelled';

  if (!prefersMobileReelSave()) {
    triggerAnchorDownload(trimmed);
    return 'downloaded';
  }

  if (canShareReelFile()) {
    try {
      const file = await fetchReelFile(trimmed, onProgress);
      if (canShareReelFile(file)) {
        try {
          await navigator.share({ files: [file], title: REEL_SHARE_TITLE });
          return 'shared';
        } catch (error) {
          if (isShareAbortError(error)) return 'cancelled';
        }
      }
    } catch {
      // CORS, timeout, size gate, OOM, or network — fall through to open-in-tab.
    }
  }

  openReelInNewTab(trimmed);
  return 'opened';
}
